import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import type { Task } from '../../services/api/tasksService';
import { healthForLookAheadTask } from './lookAheadHealth';

interface Props {
  tasks: Task[];
  loading: boolean;
}

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

export default function LookAheadTaskList({ tasks, loading }: Props) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Tasks in window
      </Typography>
      {loading && <LinearProgress />}
      {!loading && tasks.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No tasks scheduled in this window. Try widening the date range.
        </Box>
      )}
      {!loading && tasks.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Health</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((t) => {
                const health = healthForLookAheadTask(t);
                return (
                  <TableRow key={t.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {t.title}
                      </Typography>
                      {t.phase && (
                        <Typography variant="caption" color="text.secondary">
                          {t.phase.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(t.startDate)}</TableCell>
                    <TableCell>{formatDate(t.dueDate)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t.status} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={t.percentComplete}
                          sx={{ flex: 1, height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption">
                          {t.percentComplete}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={health.color} label={health.label} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
}
